// The uniform block, laid out for WebGPU and written as WGSL from the same
// function.
//
// WebGL2 looks a uniform up by name and the driver does the rest. WebGPU
// does not: the block is ONE buffer, every member sits at a byte offset, and
// the shader's `struct Uniforms` must agree with the packer to the byte. So
// the offsets and the struct text come from one place — this file — and a
// pass states only its `UniformSpec[]`, in order.
//
// The rules are the WGSL uniform address space's own:
//
//  * a member starts at the next multiple of its alignment;
//  * `vec3` aligns to 16 and takes 12, so a `float` after one sits at +12;
//  * `mat3x3f` is three 16-byte columns, so it aligns to 16 and takes 48;
//  * an array of `vec3f` or `f32` has a stride of 16 in this address space;
//  * `array<f32, N>` is ILLEGAL here, so a `float[]` packs into `vec4f`s and
//    the shader reads `U.uBandA[k / 4][k % 4]`;
//  * the whole struct rounds up to 16.
//
// A `sampler` is no member at all: it is a texture binding (`bindings.ts`).

import type { UniformSpec, UniformType } from '../prelude';
import {
  BINDING_SAMPLER_ATLAS, BINDING_SAMPLER_LINEAR, BINDING_UNIFORMS,
  GROUP_SHARED, GROUP_TEXTURES, SAMPLER_ATLAS, SAMPLER_LINEAR,
  UNIFORM_STRUCT, UNIFORM_VAR,
} from './bindings';

/** One member of the block, at its byte offset. */
export interface Member {
  readonly name: string;
  readonly type: UniformType;
  readonly offset: number;
  readonly size: number;
  /** The WGSL type the struct declares. */
  readonly wgsl: string;
  /** How many array elements, where the member is an array. */
  readonly count?: number;
}

export interface UniformLayout {
  readonly members: readonly Member[];
  /** The whole block, rounded up to 16. */
  readonly size: number;
}

/** How many numbers a spec's default carries. */
function lengthOf(spec: UniformSpec): number {
  return typeof spec.value === 'number' ? 1 : spec.value.length;
}

function roundUp(at: number, align: number): number {
  return Math.ceil(at / align) * align;
}

interface Shape { align: number; size: number; wgsl: string; count?: number }

/** The alignment, the size and the WGSL type of one spec. */
function shapeOf(spec: UniformSpec): Shape | null {
  const n = lengthOf(spec);
  switch (spec.type) {
    case 'float': return { align: 4, size: 4, wgsl: 'f32' };
    case 'int': return { align: 4, size: 4, wgsl: 'i32' };
    case 'vec2': return { align: 8, size: 8, wgsl: 'vec2f' };
    case 'ivec2': return { align: 8, size: 8, wgsl: 'vec2i' };
    case 'vec3': return { align: 16, size: 12, wgsl: 'vec3f' };
    case 'vec4': return { align: 16, size: 16, wgsl: 'vec4f' };
    case 'mat3': return { align: 16, size: 48, wgsl: 'mat3x3f' };
    case 'vec4[]': {
      const count = Math.max(1, Math.ceil(n / 4));
      return { align: 16, size: 16 * count, wgsl: `array<vec4f, ${count}>`, count };
    }
    case 'vec3[]': {
      const count = Math.max(1, Math.ceil(n / 3));
      return { align: 16, size: 16 * count, wgsl: `array<vec3f, ${count}>`, count };
    }
    case 'float[]': {
      // `array<f32, N>` is illegal in the uniform address space, so the
      // floats ride four to a `vec4f`. Four or fewer are one `vec4f`, which
      // the shader indexes as `U.uLineD[i]`.
      const count = Math.max(1, Math.ceil(n / 4));
      return count === 1
        ? { align: 16, size: 16, wgsl: 'vec4f', count }
        : { align: 16, size: 16 * count, wgsl: `array<vec4f, ${count}>`, count };
    }
    // A sampler is a texture binding and no member of the block.
    case 'sampler': return null;
  }
}

/** The byte offset of every member of one block, in SPEC ORDER. */
export function wgslLayout(specs: readonly UniformSpec[]): UniformLayout {
  const members: Member[] = [];
  let at = 0;
  for (const spec of specs) {
    const shape = shapeOf(spec);
    if (!shape) continue;
    const offset = roundUp(at, shape.align);
    members.push({
      name: spec.name,
      type: spec.type,
      offset,
      size: shape.size,
      wgsl: shape.wgsl,
      ...(shape.count === undefined ? {} : { count: shape.count }),
    });
    at = offset + shape.size;
  }
  return { members, size: Math.max(16, roundUp(at, 16)) };
}

/**
 * The `struct Uniforms { … }` text of one block.
 *
 * It is written from `wgslLayout`, so the shader's struct and the packer's
 * offsets can never disagree. Every offset is stated with `@size` left to
 * the compiler: the member order and the WGSL rules give the same numbers,
 * and the test pins them.
 */
export function wgslUniformStruct(specs: readonly UniformSpec[]): string {
  const { members } = wgslLayout(specs);
  const lines = members.map((m) => `  ${m.name}: ${m.wgsl},`);
  return `struct ${UNIFORM_STRUCT} {\n${lines.join('\n')}\n}\n`;
}

/** What one texture binding declares, from the spec's `texel`. */
function textureType(spec: UniformSpec): string {
  return spec.texel === 'u32' ? 'texture_2d<u32>' : 'texture_2d<f32>';
}

/**
 * The whole binding half of a header: the uniform block, the two samplers
 * and one texture line per sampler spec, each at its own unit.
 */
export function wgslBindings(specs: readonly UniformSpec[]): string {
  const out = [
    `@group(${GROUP_SHARED}) @binding(${BINDING_UNIFORMS}) `
      + `var<uniform> ${UNIFORM_VAR}: ${UNIFORM_STRUCT};`,
    `@group(${GROUP_SHARED}) @binding(${BINDING_SAMPLER_LINEAR}) `
      + `var ${SAMPLER_LINEAR}: sampler;`,
    `@group(${GROUP_SHARED}) @binding(${BINDING_SAMPLER_ATLAS}) `
      + `var ${SAMPLER_ATLAS}: sampler;`,
  ];
  for (const spec of specs) {
    if (spec.type !== 'sampler') continue;
    const unit = spec.unit ?? 0;
    out.push(
      `@group(${GROUP_TEXTURES}) @binding(${unit}) `
      + `var ${spec.name}: ${textureType(spec)};`,
    );
  }
  return `${out.join('\n')}\n`;
}
